import { useEffect, useRef, useState } from 'react';

import { useMapContext } from '@/context/Maps/mapComponent.context';

type MapComponentProps = {
	readonly mapOptions: any;
};
type INotarioDistance = {
	codigoNotarioNotaria: string;
	distance: number;
};
type CustomMap = google.maps.Map & {
	codigoNotarioNotaria?: string;
};

type CustomMarker = google.maps.marker.AdvancedMarkerElement & {
	codigoNotarioNotaria?: string;
};

const MapComponent = ({ mapOptions: options }: MapComponentProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const [map, setMap] = useState<CustomMap | undefined>(undefined);
	const {
		state: { notariosLocations, userLocation },
	} = useMapContext() as any; 

	const [notarioDistance, setNotarioDistance] = useState<INotarioDistance>({
		codigoNotarioNotaria: '',
		distance: 0,
	});

	const styles = {
		hide: [
			{
				featureType: 'poi',
				stylers: [{ visibility: 'off' }],
			},
			{
				featureType: 'transit',
				stylers: [{ visibility: 'off' }],
			},
		],
	};

	const isFullscreen = (element: any) => document.fullscreenElement === element;

	const exitFullscreen = async () => {
		if (document.exitFullscreen) {
			await document.exitFullscreen();
		}
	};

	const requestFullscreen = (element: any) => {
		if (element.requestFullscreen) {
			element.requestFullscreen();
		}
	};

	const createZoomButton = (currentMap: any) => {
		const zoomButton = document.createElement('button');
		const fullScreenElement = currentMap.getDiv();
		zoomButton.className = 'c-pnp-map-component__btn';
		zoomButton.addEventListener('click', async (e) => {
			e.preventDefault();
			e.stopPropagation();
			if (isFullscreen(fullScreenElement)) {
				await exitFullscreen();
			} else {
				requestFullscreen(fullScreenElement);
				currentMap.setZoom(15);
			}
		});

		return zoomButton;
	};

	useEffect(() => {
		if (ref.current && !map && window.google?.maps) {
			const initializeMapAndMarkers = async () => {
				const newMap: CustomMap = new window.google.maps.Map(ref.current!, {
					...options,
					mapId: options.mapId || 'DEMO_MAP_ID', 
				});

				const parsedUserLocation = new window.google.maps.LatLng(userLocation.userLat, userLocation.userLng);
				const referenceLocation = new window.google.maps.LatLng(
					parseFloat(options.center.lat),
					parseFloat(options.center.lng),
				);

				const zoomButtonDiv = document.createElement('div');
				const zoomButton = createZoomButton(newMap);
				zoomButtonDiv.appendChild(zoomButton);
				newMap.controls[window.google.maps.ControlPosition.TOP_LEFT].push(zoomButtonDiv);

				const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
					parsedUserLocation,
					referenceLocation,
				);
                
				setNotarioDistance({ codigoNotarioNotaria: newMap.codigoNotarioNotaria ?? '', distance });

				const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker') as google.maps.MarkerLibrary;

				const newCustomMarker = new AdvancedMarkerElement({
					position: { lat: parseFloat(options.center.lat), lng: parseFloat(options.center.lng) },
					map: newMap,
				}) as CustomMarker;

				newCustomMarker.codigoNotarioNotaria = newMap.codigoNotarioNotaria ?? '';

				newMap.setOptions({ styles: styles.hide });
				setMap(newMap);
			};

			initializeMapAndMarkers();
		}
	}, [ref, map, options, userLocation]);

	useEffect(() => {
		if (notarioDistance?.codigoNotarioNotaria) {
			const yaExiste = notariosLocations.some((n: any) => n.codigoNotarioNotaria === notarioDistance.codigoNotarioNotaria);
			if (!yaExiste) {
				notariosLocations.push(notarioDistance);
			}
		}
	}, [notarioDistance, notariosLocations]);

	return <div ref={ref} className='c-pnp-map-component'/>;
};

export default MapComponent;